import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { checkIfEmailIsValid } from '../../utils/conditional-fields-expression-properties';
import { ContactSelfServiceFormsService } from '../contact-self-service-forms.service';
import { TranslateService } from '@ngx-translate/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';

export interface IGeneralScreenSettings {
  logo?: string;
  logoBase64?: SafeUrl;
  logoPositioning?: string;
  background?: string;
  defaultContent?: string;
  footerContent?: string;
}

export interface ILandingScreenSettings extends IGeneralScreenSettings {
  landingContent?: string;
}

@Component({
  selector: 'app-landing-screen',
  templateUrl: './landing-screen.component.html',
  styleUrls: ['./landing-screen.component.scss'],
})
export class LandingScreenComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  emailGroup: FormGroup = new FormGroup({
    emailControl: new FormControl<string>('', [checkIfEmailIsValid]),
  });
  contactType: string;
  settings: ILandingScreenSettings;
  errorMessage: string = '';
  loaded: Boolean;
  private destroy$: Subject<void> = new Subject();
  browserLang: string;
  constructor(
    private contactSelfServiceFormsService: ContactSelfServiceFormsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private sanitizer: DomSanitizer,
    private titleService: Title,
    private translate: TranslateService,
    @Inject('TestRecaptchaService')
    private recaptchaV3Service: ReCaptchaV3Service
  ) {}

  ngOnInit(): void {
    this.browserLang = this.contactSelfServiceFormsService.getBrowserLang();
    /**
     * For non-aupr regions, call loadSchema only if we have fetched site key successfully.
     * If not show error toast message. This message is show only if the domain is not aupr.
     *
     * For aupr regions, checkRecaptchaKeysStatus will always be false.
     */
    if (!this.checkRecaptchaKeysStatus()) {
      this.loadSchema();
    } else {
      // show the message only is domain is not aupr
      if (this.checkDomainIsNotAUPR()) {
        this.snackBar.open('Unable to connect to reCAPTCHA. Please try again.');
      }
    }
    this.getEmail()
      .pipe(takeUntil(this.destroy$))
      .subscribe((email) => {
        if (email) {
          if (email?.length > 0) {
            // If value received is not empty, enable the submit button
            this.emailGroup.patchValue({ emailControl: email });
            this.emailGroup.markAsDirty();
          }
        }
      });
  }

  ngAfterViewInit(): void {
    this.checkEmailControlChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.errorMessage?.length > 0) {
          this.emailGroup.markAsDirty();
          this.errorMessage = '';
        }
        if (value?.length === 0) {
          this.emailGroup.markAsPristine();
          this.errorMessage = '';
        }
      });
  }

  checkEmailControlChanges(): Observable<any> {
    return this.emailGroup.controls.emailControl.valueChanges;
  }

  getEmail(): BehaviorSubject<string> {
    return this.contactSelfServiceFormsService.enteredEmail$;
  }

  checkRecaptchaKeysStatus(): boolean {
    return this.contactSelfServiceFormsService.getRecaptchaKeysStatus;
  }

  checkDomainIsNotAUPR(): boolean {
    return this.contactSelfServiceFormsService.getIsDomainNotAupr;
  }

  loadSchema(): void {
    this.loaded = false;
    this.titleService.setTitle('Contact self-service');
    this.activatedRoute.paramMap
      .pipe(
        switchMap((params) => {
          this.contactType = params.get('type');
          return this.contactSelfServiceFormsService.fetchSchema<ILandingScreenSettings>(
            'landing',
            this.contactType,
            this.browserLang
          );
        }),
        take(1),
        finalize(() => (this.loaded = true))
      )
      .subscribe(
        (schema: ILandingScreenSettings) => {
          this.settings = !isEmpty(schema) ? schema : {};
          if (this.settings.logo) {
            this.settings.logoBase64 = 'data:image/svg+xml;base64,' + this.settings.logo;
          }
        },
        (err) => {
          this.snackBar.open('Unable to load form.');
        }
      );
  }

  submit(): void {
    if (!this.checkSubmitState()) {
      this.loaded = false;
      this.contactSelfServiceFormsService
        .getReCaptchaV3Token$(this.recaptchaV3Service)
        .pipe(
          switchMap((recaptChaV3Token) => {
            return this.contactSelfServiceFormsService.verifyEmail(
              this.emailGroup.value.emailControl,
              this.contactType,
              this.browserLang,
              recaptChaV3Token
            );
          }),
          take(1),
          finalize(() => (this.loaded = true))
        )
        .subscribe(
          (_) => {
            this.getEmail().next(this.emailGroup.value.emailControl);
            this.goToEmailVerificationPage();
          },
          (err) => {
            if (err) {
              if (err?.error?.message === 'Denied') {
                this.errorMessage = 'Email address not allowed';
              }
              if (
                err?.error?.message === 'Failed to validate captcha' ||
                err?.error?.message === 'Captcha received is invalid' ||
                err?.message?.indexOf('Invalid reCAPTCHA') > -1
              ) {
                this.snackBar.open(
                  'Unable to connect to reCAPTCHA. Please try again.'
                );
              }
            }
          }
        );
    }
  }

  goToEmailVerificationPage(): void {
    this.recaptchaV3Service = null;
    this.router.navigate([
      './contact-form',
      'email-verification',
      this.contactType,
    ]);
  }

  checkSubmitState(): boolean {
    return (
      this.emailGroup.invalid ||
      this.emailGroup.pristine ||
      (this.errorMessage && this.errorMessage?.length > 0)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
